/**
 * Central navigation IA for the SALT redesign.
 * Layout should consume these definitions so desktop/mobile stay aligned.
 *
 * @see docs/REDESIGN_CHARTER.md
 * @see src/shared/navigation/iaContract.ts
 */
import type { LucideIcon } from 'lucide-react'
import {
  Home,
  Users,
  BookOpen,
  CheckSquare,
  MessageCircle,
  Calendar,
  FileText,
  TrendingUp,
  User,
  Settings,
  Shield,
} from 'lucide-react'
import {
  ADMIN_DESTINATIONS,
  MEMBER_DESTINATIONS,
  resolveSlot4NavId,
  type Slot4NavId,
} from '../../shared/navigation/iaContract'

export type NavFeatureGate = 'messaging_system' | 'journaling'

export interface NavItem {
  id: string
  label: string
  to: string
  icon: LucideIcon
  /** When set, item is wrapped in FeatureGate (desktop sidebar) */
  featureGate?: NavFeatureGate
  /** Match NavLink `end` for nested routes */
  end?: boolean
  /** Admin-only (org admin or higher) */
  requireAdmin?: boolean
  /** Super-admin only */
  requireSuperAdmin?: boolean
  /** Visual emphasis for elevated admin tools */
  tone?: 'default' | 'warning' | 'danger'
}

/**
 * Desktop sidebar — daily path first.
 * Curriculum / Events / Progress / Preferences live in profile overflow (charter slot 5).
 */
export const DESKTOP_PRIMARY_NAV: NavItem[] = [
  { id: 'home', label: 'Home', to: MEMBER_DESTINATIONS.home.webPath, icon: Home },
  { id: 'groups', label: 'Groups', to: MEMBER_DESTINATIONS.groups.webPath, icon: Users },
  { id: 'tasks', label: 'Tasks', to: MEMBER_DESTINATIONS.tasks.webPath, icon: CheckSquare },
  {
    id: 'messages',
    label: 'Messages',
    to: MEMBER_DESTINATIONS.messages.webPath,
    icon: MessageCircle,
    featureGate: 'messaging_system',
  },
  {
    id: 'journal',
    label: 'Journal',
    to: MEMBER_DESTINATIONS.journal.webPath,
    icon: FileText,
    featureGate: 'journaling',
  },
]

/** Secondary destinations — profile menu / overflow */
export const DESKTOP_OVERFLOW_NAV: NavItem[] = [
  { id: 'curriculum', label: 'Curriculum', to: MEMBER_DESTINATIONS.curriculum.webPath, icon: BookOpen },
  { id: 'events', label: 'Events', to: MEMBER_DESTINATIONS.events.webPath, icon: Calendar },
  { id: 'goals', label: 'Progress', to: '/goals', icon: TrendingUp },
  { id: 'profile', label: 'Profile', to: MEMBER_DESTINATIONS.profile.webPath, icon: User, end: true },
  { id: 'preferences', label: 'Preferences', to: '/profile/preferences', icon: Settings },
]

/**
 * Desktop admin block — task-oriented destinations (not every tool as a sibling).
 * Moderation lives under org admin shell; Enterprise under super-admin.
 */
export const DESKTOP_ADMIN_NAV: NavItem[] = [
  {
    id: 'admin',
    label: 'Admin',
    to: ADMIN_DESTINATIONS.orgAdmin.webPath,
    icon: Shield,
    requireAdmin: true,
  },
  {
    id: 'super-admin',
    label: 'Platform',
    to: ADMIN_DESTINATIONS.superAdmin.webPath,
    icon: Shield,
    requireSuperAdmin: true,
    tone: 'danger',
  },
  {
    id: 'enterprise-admin',
    label: 'Enterprise',
    to: ADMIN_DESTINATIONS.enterpriseAdmin.webPath,
    icon: Shield,
    requireSuperAdmin: true,
    tone: 'danger',
  },
]

/** Slot-4 Journal candidate (web bottom nav when journaling is enabled) */
export const SLOT_4_JOURNAL_NAV: NavItem = {
  id: 'journal',
  label: 'Journal',
  to: MEMBER_DESTINATIONS.journal.webPath,
  icon: FileText,
}

/** Slot-4 Messages candidate (fallback when journaling is off; Expo default) */
export const SLOT_4_MESSAGES_NAV: NavItem = {
  id: 'messages',
  label: 'Messages',
  to: MEMBER_DESTINATIONS.messages.webPath,
  icon: MessageCircle,
}

function slot4NavItem(slot: Slot4NavId): NavItem {
  return slot === 'journal' ? SLOT_4_JOURNAL_NAV : SLOT_4_MESSAGES_NAV
}

/**
 * Build the five-slot mobile bottom nav for the current feature-flag state.
 * Always returns exactly five items — never hides slot 4.
 */
export function resolveMobileBottomNav(journalingEnabled: boolean): NavItem[] {
  const slot4 = resolveSlot4NavId({ journalingEnabled })
  return [
    { id: 'home', label: 'Home', to: MEMBER_DESTINATIONS.home.webPath, icon: Home },
    { id: 'groups', label: 'Groups', to: MEMBER_DESTINATIONS.groups.webPath, icon: Users },
    { id: 'tasks', label: 'Tasks', to: MEMBER_DESTINATIONS.tasks.webPath, icon: CheckSquare },
    slot4NavItem(slot4),
    { id: 'profile', label: 'Profile', to: MEMBER_DESTINATIONS.profile.webPath, icon: User },
  ]
}

/**
 * Static default for tests / SSR — Journal in slot 4 (historical web PWA when journaling on).
 * Runtime UI should use `resolveMobileBottomNav(journalingEnabled)`.
 */
export const MOBILE_BOTTOM_NAV: NavItem[] = resolveMobileBottomNav(true)
